import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Gift, KeyRound, Copy, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import Mascot from "@/components/Mascot";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function ForgotPasswordDialog({ open, onOpenChange, onLoggedIn, prefillEmail }) {
  const [step, setStep] = useState("request"); // request | reset
  const [email, setEmail] = useState(prefillEmail || "");
  const [token, setToken] = useState("");
  const [issuedToken, setIssuedToken] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("request");
      setEmail(prefillEmail || "");
      setToken("");
      setIssuedToken(null);
      setNewPw("");
      setConfirm("");
      setShowNewPw(false);
    }
  }, [open, prefillEmail]);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot", { email });
      if (data.sent && data.token) {
        setIssuedToken(data.token);
        setToken(data.token);
        setStep("reset");
        toast.success("Code de réinitialisation généré");
      } else {
        toast.info("Si cet email existe, un code a été envoyé.");
        setStep("reset"); // still let them paste a token they got elsewhere
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset", { token, password: newPw });
      localStorage.setItem("parentia_token", data.token);
      toast.success("Mot de passe mis à jour · connexion automatique");
      onOpenChange(false);
      if (onLoggedIn) await onLoggedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lien invalide ou expiré");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="forgot-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#23375E]" />
            Récupération de mot de passe
          </DialogTitle>
          <DialogDescription>
            {step === "request"
              ? "Entre ton email pour recevoir un code de réinitialisation."
              : "Colle le code reçu puis choisis un nouveau mot de passe."}
          </DialogDescription>
        </DialogHeader>

        {step === "request" ? (
          <form onSubmit={request} className="space-y-4">
            <div>
              <Label htmlFor="forgot-email">Email du compte</Label>
              <Input
                id="forgot-email"
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="marie@exemple.fr"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="send-code-btn"
              disabled={busy}
              className="w-full brand-btn rounded-full py-5 font-semibold"
            >
              {busy ? "..." : "Envoyer le code"}
            </Button>
            <p className="text-xs text-center text-[#77849C]">
              💡 En production, ce code est envoyé par email. Ici, il apparaît directement pour la démo.
            </p>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {issuedToken && (
              <div className="rounded-2xl bg-[#FFF4DC] p-3" data-testid="issued-token-box">
                <p className="text-xs font-bold uppercase tracking-wider text-[#16233D]">
                  Ton code (démo — 15 min)
                </p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="font-mono text-[#16233D] break-all text-sm">{issuedToken}</code>
                  <button
                    type="button"
                    data-testid="copy-token-btn"
                    onClick={() => { navigator.clipboard.writeText(issuedToken); toast.success("Code copié"); }}
                    className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-semibold hover:bg-[#EDF2F9] transition inline-flex items-center gap-1"
                  >
                    <Copy className="h-3 w-3" /> Copier
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="reset-token">Code de réinitialisation</Label>
              <Input
                id="reset-token"
                data-testid="reset-token-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Colle le code ici"
                required
              />
            </div>
            <div>
              <Label htmlFor="new-pw">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="new-pw"
                  data-testid="new-password-input"
                  type={showNewPw ? "text" : "password"}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  minLength={6}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-new-password-visibility"
                  onClick={() => setShowNewPw((v) => !v)}
                  aria-label={showNewPw ? "Masquer" : "Afficher"}
                  className="absolute inset-y-0 right-2 my-auto h-8 w-8 grid place-items-center rounded-full text-[#77849C] hover:text-[#16233D] hover:bg-[#EDF2F9] transition"
                >
                  {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm-pw">Confirmer</Label>
              <Input
                id="confirm-pw"
                data-testid="confirm-password-input"
                type={showNewPw ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={6}
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="reset-password-btn"
              disabled={busy}
              className="w-full brand-btn rounded-full py-5 font-semibold"
            >
              {busy ? "..." : "Réinitialiser et me connecter"}
            </Button>
            <button
              type="button"
              onClick={() => setStep("request")}
              className="w-full text-center text-xs text-[#77849C] underline"
            >
              Renvoyer un code
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AuthPage() {
  const { login, register, refresh } = useAuth();
  const [params] = useSearchParams();
  const initialRef = params.get("ref") || "";
  const [mode, setMode] = useState(initialRef ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [refCode, setRefCode] = useState(initialRef.toUpperCase());
  const [refInfo, setRefInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Le code de parrainage est mémorisé pour survivre au détour par Google Auth
  useEffect(() => {
    if (initialRef) localStorage.setItem("kynoria_ref", initialRef.toUpperCase());
  }, [initialRef]);

  useEffect(() => {
    if (!refCode || refCode.length < 5) {
      setRefInfo(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/referrals/validate/${refCode}`);
        setRefInfo({ valid: true, name: data.referrer_name });
        localStorage.setItem("kynoria_ref", refCode);
      } catch {
        setRefInfo({ valid: false });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Bienvenue !");
      } else {
        await register(email, password, name, refCode || null);
        toast.success(
          refCode && refInfo?.valid
            ? `Bienvenue ! 3 jours d'essai offerts grâce à ${refInfo.name} 🎁`
            : "Compte créé, bienvenue !"
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 fade-up">
          <img
            src="/logo-kynoria.png"
            alt="Kynoria"
            className="mx-auto mb-4 h-28 w-28 object-contain"
            style={{ background: "transparent" }}
            data-testid="auth-logo"
          />
          <h1 className="text-4xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Kynoria
          </h1>
          <p className="mt-2 text-[#77849C]">
            Votre coach parental doux et ludique, pour chaque âge.
          </p>
          <div className="mt-2 flex items-end justify-center">
            <Mascot pose="hello" size={150} anim="sway" className="mascot-pop" />
          </div>
        </div>

        <div className="soft-card p-6 sm:p-8 fade-up">
          <div className="flex bg-[#EDF2F9] p-1 rounded-full mb-6">
            <button data-testid="tab-login" onClick={() => setMode("login")}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition ${mode === "login" ? "bg-white text-[#16233D] shadow-sm" : "text-[#77849C]"}`}>
              Connexion
            </button>
            <button data-testid="tab-register" onClick={() => setMode("register")}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition ${mode === "register" ? "bg-white text-[#16233D] shadow-sm" : "text-[#77849C]"}`}>
              Inscription
            </button>
          </div>

          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 rounded-full border border-[#E3E8F1] bg-white px-5 py-3 text-[#16233D] font-medium hover:bg-[#EDF2F9] transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-5 w-5">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.2 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.2 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 35.6 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/>
            </svg>
            Continuer avec Google
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-[#77849C]">
            <div className="flex-1 h-px bg-[#E3E8F1]" />
            ou avec ton email
            <div className="flex-1 h-px bg-[#E3E8F1]" />
          </div>

          {mode === "register" && refCode.length >= 5 && refInfo && (
            <div
              className={`mb-4 rounded-2xl p-3 flex items-center gap-2 ${
                refInfo.valid ? "bg-[#D2EDE1]" : "bg-[#FBE2E9]"
              }`}
              data-testid="referral-preview"
            >
              <Gift className={`h-5 w-5 ${refInfo.valid ? "text-[#16233D]" : "text-[#E8899F]"}`} />
              {refInfo.valid ? (
                <p className="text-sm text-[#16233D]">
                  Invité·e par <strong>{refInfo.name}</strong> · 3 jours d'essai offerts 💛
                </p>
              ) : (
                <p className="text-sm text-[#E8899F]">
                  Code parrain non reconnu — l'inscription reste possible, mais sans les 3 jours d'essai.
                </p>
              )}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Prénom</Label>
                <Input data-testid="name-input" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Marie" required />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input data-testid="email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="marie@exemple.fr" required />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  data-testid="password-input"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute inset-y-0 right-2 my-auto h-8 w-8 grid place-items-center rounded-full text-[#77849C] hover:text-[#16233D] hover:bg-[#EDF2F9] transition"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {mode === "login" && (
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => setForgotOpen(true)}
                  className="mt-2 text-xs text-[#23375E] hover:text-[#16233D] underline"
                >
                  Mot de passe oublié ?
                </button>
              )}
            </div>
            {mode === "register" && (
              <div>
                <Label htmlFor="refcode" className="flex items-center gap-1">
                  <Gift className="h-4 w-4 text-[#E8899F]" /> Code parrain — 3 jours d'essai offerts
                </Label>
                <Input
                  data-testid="ref-code-input"
                  id="refcode"
                  value={refCode}
                  onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                  placeholder="MARIE-A1B2C3"
                />
                {refCode.length >= 5 && refInfo && !refInfo.valid && (
                  <p className="mt-1 text-xs text-[#E8899F]">Code invalide</p>
                )}
              </div>
            )}
            <Button data-testid="submit-auth-btn" type="submit" disabled={busy} className="w-full brand-btn rounded-full py-6 text-base font-semibold">
              {busy ? "..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>
        </div>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        onLoggedIn={refresh}
        prefillEmail={email}
      />
    </div>
  );
}
